// Sign-in with Google or Strava, linked accounts, uploading activities to Strava.
// The flow leaves the app (provider page) then comes back through #/signin/<provider>/<code>.
import { newNonce } from '@gruvetto/browser';
import type { AccountsResponse, MailLang, OAuthFinishResponse, OAuthProvider, ProvidersResponse, StravaUpload } from '@gruvetto/contracts';
import { api, adoptSession } from './api.js';

const KEY = 'gv-oauth-v1';
interface Pending { provider: OAuthProvider; intent: 'login' | 'link'; nonce: string }

let providersCache: Promise<ProvidersResponse> | null = null;
/** Providers configured on the server; none if the server is unreachable. */
export function providers(): Promise<ProvidersResponse> {
  providersCache ??= api<ProvidersResponse>('/auth/providers', { auth: false }).catch(() => { providersCache = null; return { google: false, strava: false }; });
  return providersCache;
}

/** Goes to the provider. The nonce stays in this tab (sessionStorage): only this tab can finish the flow. */
export async function startOAuth(provider: OAuthProvider, intent: 'login' | 'link') {
  const nonce = newNonce();
  const { url } = await api<{ url: string }>(`/auth/oauth/${provider}/start`, { method: 'POST', body: { intent, nonce }, auth: intent === 'link' });
  try { sessionStorage.setItem(KEY, JSON.stringify({ provider, intent, nonce } satisfies Pending)); } catch {}
  location.assign(url);
}

/** Finishes the flow on return: opens the session, or links the account. Null if this tab did not start it. */
export async function finishOAuth(provider: OAuthProvider, code: string): Promise<(OAuthFinishResponse & { intent: Pending['intent'] }) | null> {
  const p = takePending(provider);
  if (!p) return null;
  const r = await api<OAuthFinishResponse>('/auth/oauth/finish', { method: 'POST', body: { code, nonce: p.nonce }, auth: p.intent === 'link' });
  if (r.kind === 'session') adoptSession(r.session);
  return { ...r, intent: p.intent };
}

/** Reads and clears this tab's pending flow. */
export function takePending(provider: OAuthProvider): Pending | null {
  try {
    const p = JSON.parse(sessionStorage.getItem(KEY) || 'null') as Pending | null;
    sessionStorage.removeItem(KEY);
    return p && p.provider === provider ? p : null;
  } catch { return null; }
}

export const linkedAccounts = () => api<AccountsResponse>('/me/accounts');
/** Unlinks a provider; the password confirms it when the account has one (ADR 0030), `lang` picks the language of the notice e-mailed. */
export const unlinkAccount = (provider: OAuthProvider, password: string | undefined, lang: MailLang) => api(`/me/accounts/${provider}`, { method: 'DELETE', body: { password, lang } });
export const setStravaAutoUpload = (autoUpload: boolean) => api('/me/accounts/strava', { method: 'PATCH', body: { autoUpload } });
export const stravaStatus = (activityId: string) => api<StravaUpload>(`/activities/${encodeURIComponent(activityId)}/strava`);
export const uploadToStrava = (activityId: string) => api<StravaUpload>(`/activities/${encodeURIComponent(activityId)}/strava`, { method: 'POST' });
export const stravaActivityUrl = (id: string) => `https://www.strava.com/activities/${encodeURIComponent(id)}`;
